/**
 * All API urls and other constants will reside here.
 * It is always a good idea to keep a local copy of all API response to
 * keep your app working for UI changes and
 * make it independent of network requirements.
 *
 * They need to be categorised and grouped together as:
 *  - Actual endpoints url.
 *  - Local data .json file path.
 * At a moment only one group should be uncommented.
 *
 * Other way to deal with this is to name every json file as per your service endpoint and use a basepath variable.
 * Toggle this basePath variable between "actual-domain.com/" or "/data/".
 */

// Actual endpoints. Uncomment below section to use actual data.
// export const GET_ALL_USERS = () => `https://jsonplaceholder.typicode.com/users`;
// export const GET_USER_DETAILS = (id) => `https://jsonplaceholder.typicode.com/users/${id}`;

// Local endpoints. Uncomment below section to use dummy local data.

export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://localhost:8089/sh_org_tenant_mgmt_service/api/v1"

export const NO_RECORDES_FOUND = "There is no record found!"

// ==========================================
// Organization API paths
// ==========================================
export const CREATE_ORGANIZATION_PATH = () => {
  return `${API_BASE_URL}/organizations`
}
export const GET_ORGANIZATION_PATH = () => {
  return `${API_BASE_URL}/organizations`
}
export const GET_ORGANIZATIONS_PATH = (orgId) => {
  return `${API_BASE_URL}/organizations/${orgId}`
}
export const UPDATE_ORGANIZATIONS_PATH = (orgId) => {
  return `${API_BASE_URL}/organizations/${orgId}`
}

// ==========================================
// Site API paths
// ==========================================
export const CREATE_SITE_PATH = () => {
  return `${API_BASE_URL}/sites`
}
export const GET_SITES_BY_ORGANIZATION_PATH = (orgId) => {
  return `${API_BASE_URL}/sites/organization/${orgId}`
}
export const GET_SITE_PATH = (siteId) => {
  return `${API_BASE_URL}/sites/${siteId}`
}
export const UPDATE_SITE_PATH = (siteId) => {
  return `${API_BASE_URL}/sites/${siteId}`
}
export const DELETE_SITE_PATH = (siteId) => {
  return `${API_BASE_URL}/sites/${siteId}`
}

// ==========================================
// Floor API paths
// ==========================================
export const CREATE_FLOOR_PATH = () => {
  return `${API_BASE_URL}/floors`
}
export const GET_FLOORS_BY_SITE_PATH = (siteId) => {
  return `${API_BASE_URL}/floors/site/${siteId}`
}
export const UPDATE_FLOOR_PATH = (floorId) => {
  return `${API_BASE_URL}/floors/${floorId}`
}
export const DELETE_FLOOR_PATH = (floorId) => {
  return `${API_BASE_URL}/floors/${floorId}`
}

// ==========================================
// Space API paths
// ==========================================
export const CREATE_SPACE_PATH = () => {
  return `${API_BASE_URL}/spaces`
}
export const GET_SPACES_BY_FLOOR_PATH = (floorId) => {
  return `${API_BASE_URL}/spaces/floor/${floorId}`
}
export const UPDATE_SPACE_PATH = (spaceId) => {
  return `${API_BASE_URL}/spaces/${spaceId}`
}
export const DELETE_SPACE_PATH = (spaceId) => {
  return `${API_BASE_URL}/spaces/${spaceId}`
}

// ==========================================
// Dashboard API paths
// ==========================================
export const FETCH_DASHBOARD_DATA_PATH = () => {
  return `${API_BASE_URL}/dashboard`
}

// ==========================================
// Space Types API paths
// ==========================================
export const GET_SPACE_TYPES_PATH = () => {
  return `${API_BASE_URL}/space-types`
}

// ==========================================
// Device API paths
// ==========================================
export const VALIDATE_DEVICE_NAME_PATH = (deviceName) => {
  return `${API_BASE_URL}/devices/validate?deviceName=${encodeURIComponent(deviceName)}`
}
export const SAVE_DEVICE_USER_ASSOCIATION_PATH = () => {
  return `${API_BASE_URL}/users/deviceUserAssociation`
}
export const GET_DEVICES_PATH = () => {
  return `${API_BASE_URL}/users/deviceUserAssociation`
}

export const UPDATE_DEVICE_ASSOCIATION_PATH = (id) => {
  return `${API_BASE_URL}/users/deviceUserAssociation/${id}`
}
export const GET_CAMERAS_PATH = () => {
  return `${API_BASE_URL}/cameras`
}
export const GET_DEVICE_STREAM_URL_PATH = (deviceId) => {
  return `${API_BASE_URL}/devices/${deviceId}/stream-url`
}
export const SEND_STREAMING_COMMAND_PATH = () => {
  return `${API_BASE_URL}/devices/streaming`
}
export const GET_HUBS_BY_SITE_PATH = () => {
  return `${API_BASE_URL}/users/deviceUserAssociation/hubs`
}
export const INITIATE_NVR_ONBOARDING_PATH = () => {
  return `${API_BASE_URL}/nvr/onboard`
}

export const DISCOVER_CAMERAS_PATH = (nvrId) => {
  return `${API_BASE_URL}/nvr/${nvrId}/discovery/initiate`
}
export const GET_ONBOARDING_STATUS_PATH = (commandId) => {
  return `${API_BASE_URL}/nvr/onboarding-status/${commandId}`
}
export const GET_DISCOVERY_CANDIDATES_PATH = (jobId) => {
  return `${API_BASE_URL}/nvr/discovery/${jobId}/candidates`
}
export const PROVISION_CAMERAS_PATH = () => {
  return `${API_BASE_URL}/nvr/discovery/provision`
}

export const DECOMMISSIONED_DEVICES_PATH = () => {
  return `${API_BASE_URL}/users/deviceUserAssociation/decommissioned`
}

export const MEMBERS_PATH = () => {
  return `${API_BASE_URL}/users/members`
}

// ==========================================
// Catalog API paths
// ==========================================
export const GET_CATALOGS_PATH = (params) =>
  `/catalogs?${new URLSearchParams(params || {}).toString()}`
export const GET_CATALOGS_ALL_PATH = () => `/catalogs/all`
export const CREATE_CATALOG_PATH = () => `/catalogs`
export const GET_CATALOG_PATH = (id) => `/catalogs/${id}`
export const UPDATE_CATALOG_PATH = (id) => `/catalogs/${id}`
export const DELETE_CATALOG_PATH = (id) => `/catalogs/${id}`

export const GET_DEVICE_CATEGORIES_PATH = (params) =>
  `/catalogs/categories?${new URLSearchParams(params || {}).toString()}`
export const GET_DEVICE_CATEGORIES_BY_CATALOG_PATH = (catalogId) =>
  `/catalogs/${catalogId}/categories`
export const CREATE_DEVICE_CATEGORY_PATH = (catalogId) =>
  `/catalogs/${catalogId}/categories`
export const GET_DEVICE_CATEGORY_PATH = (id) => `/catalogs/categories/${id}`
export const UPDATE_DEVICE_CATEGORY_PATH = (id) => `/catalogs/categories/${id}`
export const DELETE_DEVICE_CATEGORY_PATH = (id) => `/catalogs/categories/${id}`

export const GET_MODEL_GROUPS_PATH = (params) =>
  `/groups?${new URLSearchParams(params || {}).toString()}`
export const GET_MODEL_GROUPS_BY_CATEGORY_PATH = (categoryId) =>
  `/categories/${categoryId}/groups`
export const CREATE_MODEL_GROUP_PATH = (categoryId) =>
  `/categories/${categoryId}/groups`
export const UPDATE_MODEL_GROUP_PATH = (id) => `/groups/${id}`
export const DELETE_MODEL_GROUP_PATH = (id) => `/groups/${id}`

export const GET_DEVICE_MODELS_PATH = (params) =>
  `/models?${new URLSearchParams(params || {}).toString()}`
export const CREATE_DEVICE_MODEL_PATH = (groupId) => `/groups/${groupId}/models`
export const GET_DEVICE_MODEL_PATH = (id) => `/models/${id}`
export const UPDATE_DEVICE_MODEL_PATH = (id) => `/models/${id}`
export const DELETE_DEVICE_MODEL_PATH = (id) => `/models/${id}`

// ==========================================
// WHEP Stream Proxy paths
// ==========================================
export const WHEP_PROXY_PATH = (streamPath, target) => {
  let url = `${API_BASE_URL}/stream/whep-proxy?streamPath=${encodeURIComponent(streamPath)}`
  if (target) url += `&target=${encodeURIComponent(target)}`
  return url
}
export const WHEP_SESSION_DELETE_PATH = (sessionUrl, target) => {
  let url = `${API_BASE_URL}/stream/whep-session?sessionUrl=${encodeURIComponent(sessionUrl)}`
  if (target) url += `&target=${encodeURIComponent(target)}`
  return url
}

// ==========================================
// WebSocket API paths
// ==========================================
export const GET_DEVICE_ASSOCIATION_WS_PATH = () => {
  return `/deviceAssociation`
}

export const GET_SESSION_EVENTS_WS_PATH = () => {
  return `/ws/session-events`
}

// ==========================================
// Playback API paths
// ==========================================

export const JUMP_PLAYBACK_PATH = () => {
  return `${API_BASE_URL}/nvr/playback/jump`
}

export const GET_PLAYBACK_CHUNK_PATH = () => {
  return `${API_BASE_URL}/nvr/playback/seek`
}

export const GET_PLAYBACK_DATES_PATH = () => {
  return `${API_BASE_URL}/nvr/playback/dates`
}

// ==========================================
// RBAC API paths
// ==========================================
export const GET_MY_PERMISSIONS_PATH = () => {
  return `${API_BASE_URL}/my-permissions`
}

export const GET_ROLES_PATH = () => {
  return `${API_BASE_URL}/roles`
}

export const GET_PERMISSIONS_PATH = () => {
  return `${API_BASE_URL}/permissions`
}

export const CREATE_MEMBER_PATH = () => {
  return `${API_BASE_URL}/members`
}

export const DELETE_MEMBER_PATH = (userId) => {
  return `${API_BASE_URL}/members/${userId}`
}
// Member API paths
export const GET_MEMBERS_PATH = () => {
  return `${API_BASE_URL}/members`
}

export const GET_MEMBER_INVITATIONS_PATH = () => {
  return `${API_BASE_URL}/members/invitations`
}

export const UPDATE_MEMBER_STATUS_PATH = (userId) => {
  return `${API_BASE_URL}/members/${userId}/status`
}

export const EDIT_MEMBER_ROLE_PATH = (userId) => {
  return `${API_BASE_URL}/members/${userId}/role`
}

export const GET_MEMBER_AUDIT_HISTORY_PATH = (userId) => {
  return `${API_BASE_URL}/members/${userId}/audit-history`
}

// events
export const GET_EVENTS_PATH = (params) => {
  return `${API_BASE_URL}/events?${new URLSearchParams(params || {}).toString()}`
}

export const UPDATE_WORKFLOW_STATUS_PATH = (eventId) => {
  return `${API_BASE_URL}/events/${eventId}/workflow-status`
}

export const GET_EVENT_SEVERITY_COUNT_PATH = (params) => {
  return `${API_BASE_URL}/events/severity-counts?${new URLSearchParams(params || {}).toString()}`
}
